import React, { useState, useCallback, useEffect } from 'react'
import { Image, Paragraph, Button, H3, useOverlay, WhiteTheme } from '@fs/zion-ui'
import { css } from '@emotion/core'
import Separator from '@fs/zion-ui/dist/cjs/separator'
import { i18n } from '@fs/zion-locale'
import { useTranslation, Trans } from 'react-i18next'
import { Link, useQueryParams } from '@fs/zion-router'
import defaultFamilyTreeDesign from './assets/ginkgo-tree-generic.svg'
import chineseTreeTitle from './assets/chinese-family-tree-title.svg'
import ToTreeDesignsOverlay from '../../components/ToTreeDesignsOverlay/ToTreeDesignsOverlay'
import ImagePicker from '../../components/PageAssembler/InnerComponents/ImagePicker/ImagePicker'
import { useMemoryBook } from '../../providers/MemoryBookProvider'
import { getHasDarkBackground } from '../../helpers/getHasDarkBackground'

const columnCss = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 95%;
  width: 100%;
`

const imagePickerWrapperCss = css`
  height: 100%;
  width: 100%;
  max-height: 500px;

  @media print {
    max-height: 100%;
  }

  @media print and (orientation: landscape) {
    img {
      max-width: 600px;
    }
  }
`

const TreeTitle = ({ isPrintView }) => {
  const [t] = useTranslation()
  const isChinese = i18n.language === 'zh'

  const containerStyle = css({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    marginTop: '1em',
    marginBottom: '3em',
  })

  const chineseTitle = isPrintView ? (
    <div css={containerStyle}>
      <img
        src={chineseTreeTitle}
        alt={t('TreeDesignsPage.title.myFamilyTree', 'My Family Tree')}
        style={{ minWidth: '450px' }}
      />
    </div>
  ) : (
    <Image
      src={chineseTreeTitle}
      style={{ maxWidth: '75%', display: 'flex', justifyContent: 'center' }}
      alt={t('TreeDesignsPage.title.myFamilyTree', 'My Family Tree')}
    />
  )

  const nonChineseTitle = isPrintView ? (
    <div css={containerStyle}>
      <H3 centered>{t('TreeDesignsPage.title.myFamilyTree', 'My Family Tree')}</H3>
    </div>
  ) : (
    <H3 centered>{t('TreeDesignsPage.title.myFamilyTree', 'My Family Tree')}</H3>
  )

  return isChinese ? chineseTitle : nonChineseTitle
}

const TreeDesignsPage = ({ pageNumber, memoryId, isPrintView = false }) => {
  const toTreeDesignsOverlay = useOverlay()
  const [t] = useTranslation()
  const [isMemoryMissing, setIsMemoryMissing] = useState(false)
  const { query, clearQuery } = useQueryParams()
  const uploadedDesignId = query?.uploadedDesignId || null
  const queryParameterPageNumber = Number(query?.pageNumber) || 0
  const { updatePageContent, memoryBook } = useMemoryBook()

  const hasDarkBackground = getHasDarkBackground(pageNumber, memoryBook)

  useEffect(() => {
    if (pageNumber === queryParameterPageNumber && uploadedDesignId) {
      const updateContent = async () => {
        await updatePageContent(pageNumber, 0, { memoryId: uploadedDesignId })
        clearQuery()
      }
      updateContent()
    }
  }, [pageNumber, queryParameterPageNumber, uploadedDesignId, updatePageContent, clearQuery])

  const handleMissingMemoryStatus = useCallback(
    (missing) => {
      setIsMemoryMissing(missing)
    },
    [setIsMemoryMissing]
  )

  const getCaptionText = () => {
    const initialCaption = memoryId
      ? t(
          'TreeDesignsPage.captionText.alreadyHaveUploaded',
          'Try different Tree Design styles and add more family members to your tree.'
        )
      : t(
          'TreeDesignsPage.captionText.firstTime',
          'Create and upload a beautiful family tree by visiting Tree Designs.'
        )

    return isMemoryMissing ? (
      <Trans
        components={[<Link key="toMemoryGallery" to="memories/gallery" external />]}
        defaults="This image may have been deleted or need modification in your <0>Memory Gallery</0>."
        i18nKey="TreeDesignsPage.captionText.linkBroken"
      />
    ) : (
      initialCaption
    )
  }

  const captionText = getCaptionText()

  return (
    <div css={columnCss}>
      <TreeTitle isPrintView={isPrintView} />
      <Separator size="sm" />
      <div css={imagePickerWrapperCss}>
        <ImagePicker
          imageSource={!memoryId && defaultFamilyTreeDesign}
          noCaption
          pageNumber={pageNumber}
          type="image"
          memoryId={uploadedDesignId || memoryId}
          isPrintView={isPrintView}
          isTreeDesignsPage
          handleMissingMemoryStatus={handleMissingMemoryStatus}
        />
      </div>
      {isPrintView ? null : (
        <>
          <Separator size="nano" />
          {hasDarkBackground && (
            <WhiteTheme>
              <Paragraph centered size="sm" secondary>
                {captionText}
              </Paragraph>
              <Separator size="sm" />
              <Button emphasis="medium" centered onClick={toTreeDesignsOverlay.open}>
                {t('TreeDesignsPage.button.familyTreeDesigns', 'Family Tree Designs')}
              </Button>
            </WhiteTheme>
          )}
          {!hasDarkBackground && (
            <>
              <Paragraph centered size="sm" secondary>
                {captionText}
              </Paragraph>
              <Separator size="sm" />
              <Button emphasis="medium" centered onClick={toTreeDesignsOverlay.open}>
                {t('TreeDesignsPage.button.familyTreeDesigns', 'Family Tree Designs')}
              </Button>
            </>
          )}
        </>
      )}

      {(toTreeDesignsOverlay.isOpen || toTreeDesignsOverlay.transitioning) && (
        <ToTreeDesignsOverlay overlay={toTreeDesignsOverlay} pageNumber={pageNumber} />
      )}
    </div>
  )
}

export default TreeDesignsPage
